// import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useRouterState,
} from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import z from "zod";
/**
 * Protected Layout Route
 *
 * This layout route implements centralized authentication protection for all child routes.
 * Any route placed under this layout will automatically require authentication.
 *
 * Features:
 * - Centralized authentication guard using beforeLoad
 * - Automatic redirect to login for unauthenticated users
 * - Loading state handling during authentication checks
 * - Error boundary for authentication failures
 */
import { Button } from "@/components/base/buttons/button";
import { NotFound } from "@/components/Organisms/NotFound";
import UserProfileDrawer from "@/components/Organisms/UserProfileDrawer/UserProfileDrawer";
import { waitForConfig } from "@/config";
import { useAuth } from "@/contexts/AuthProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import {
	ensureAuthenticatedI18n,
	shouldSkipRedundantFieldInfoLocalizationsApply,
} from "@/i18n-authenticated";
import { queryClient } from "@/integrations/tanstack-query/root-provider";
import {
	createAppFieldRenderers,
	RendererProvider,
} from "@/lib/dynamicForms";
import { authQueryOptions } from "@/services/auth/auth.options";
import type { UserInfo } from "@/services/auth/auth.types";
import { useAuthStatus } from "@/stores/auth-hooks";
import {
	handleMissingChunkError,
	isMissingChunkError,
} from "@/utils/reload-on-missing-chunk";
import { getAuthState } from "@/stores/auth-store";
import { logApp } from "@/utils/logApp";
import { buildLoginRedirect } from "@/utils/route-guards";

const appFieldRenderers = createAppFieldRenderers();

function resolveOrganizationIdForLocalizationsSync(
	params: Record<string, string | undefined>,
): string | undefined {
	// `orgId` is the working organization (web and org-admin).
	// `organizationId` is the platform admin drawer entity, not a localization scope.
	const fromParams = typeof params.orgId === "string" ? params.orgId : undefined;
	if (fromParams) return fromParams;

	const active = getAuthState().activeOrganizationId;
	if (active) return active;

	const userInfo = queryClient.getQueryData(
		authQueryOptions.userInfo().queryKey,
	) as UserInfo | undefined;
	if (!userInfo) return undefined;

	return (
		userInfo.currentOrganizationId ??
		userInfo.organizationUserInfo?.[0]?.organization?.id ??
		undefined
	);
}

async function ensureAuthenticatedI18nForRoute(
	params: Record<string, string | undefined>,
) {
	let organizationId = resolveOrganizationIdForLocalizationsSync(params);
	if (!organizationId) {
		const userInfo = await queryClient.ensureQueryData(
			authQueryOptions.userInfo(),
		);
		organizationId =
			userInfo.currentOrganizationId ??
			userInfo.organizationUserInfo?.[0]?.organization?.id ??
			undefined;
	}
	await ensureAuthenticatedI18n(organizationId);
}

// Search params schema for the authenticated layout
const AuthenticatedSearchSchema = z.object({
	profileDrawer: z.string().optional(),
	profileDrawerTab: z.string().optional(),
});

/**
 * Protected Layout Route Definition
 *
 * The beforeLoad function runs before any child route loads, ensuring
 * that authentication is checked at the layout level rather than
 * in individual route components.
 */
export const Route = createFileRoute("/_authenticated")({
	/**
	 * Authentication guard - runs before any child route loads
	 *
	 * This is the centralized protection layer that all protected routes inherit.
	 * It ensures that users are authenticated before accessing any protected content.
	 *
	 * Now uses Zustand auth state through router context for convenient access,
	 * providing better performance while maintaining familiar context API.
	 */
	beforeLoad: async ({ location, params, preload, cause }) => {
		if (import.meta.env.SSR) return;

		// Wait for runtime config before any API calls — config loads asynchronously on
		// fresh page loads and OAuth-callback client-side navigations alike. Without this
		// guard, ensureQueryData(userInfo) fires before getConfig() is ready and throws.
		await waitForConfig();

		// ✅ Access Zustand auth state through router context
		const auth = getAuthState();

		/**
		 * Apply field-info localizations to i18next.
		 * Skip when `preload` is true (new segment preload). When hovering links, parent `_authenticated`
		 * often has `preload: false` but `cause: "stay"` — skip redundant `ensureQueryData` + apply
		 * if we already applied for the same resolved org (avoids stale cache reverting terminology).
		 */
		const applyI18nIfNotPreload = async () => {
			if (preload) return;
			const resolved = resolveOrganizationIdForLocalizationsSync(params);
			if (shouldSkipRedundantFieldInfoLocalizationsApply(resolved, cause)) {
				return;
			}
			await ensureAuthenticatedI18nForRoute(params);
		};
		// console.warn(" _authenticated beforeLoad - Zustand auth via context:", {
		// 	isAuthenticated: auth.isAuthenticated,
		// 	isLoading: auth.isLoading,
		// 	isInitializing: auth.isInitializing,
		// 	hasUser: !!auth.user,
		// 	pathname: location.pathname,
		// });

		// If authenticated and permissions loaded, allow access immediately
		if (auth.isAuthenticated && auth.user && auth.isPermissionsLoaded) {
			await queryClient.ensureQueryData(authQueryOptions.userInfo());
			await applyI18nIfNotPreload();
			console.log(
				`✅ User authenticated with permissions - allowing access to ${location.pathname}`,
			);
			return;
		}

		// If authenticated but permissions not loaded, wait for them
		if (auth.isAuthenticated && auth.user && !auth.isPermissionsLoaded) {
			// console.log(
			// 	"⏳ User authenticated but permissions not loaded, waiting...",
			// );
			// The loading will be handled by the auth store, just wait a bit
			await new Promise((resolve) => setTimeout(resolve, 100));

			// Re-check after waiting
			const updatedAuth = getAuthState();
			if (updatedAuth.isPermissionsLoaded) {
				await queryClient.ensureQueryData(authQueryOptions.userInfo());
				await applyI18nIfNotPreload();

				console.log(
					`✅ Permissions loaded - allowing access to ${location.pathname}`,
				);
				return;
			}
		}

		// If we're coming from OAuth callback and auth state is not set,
		// try to fetch user info to verify authentication
		if (!auth.isAuthenticated && !auth.isLoading && !auth.isInitializing) {
			console.log("🔄 Auth state not confirmed, checking with API...");

			try {
				// Try to fetch user info to confirm authentication
				const userInfo = await queryClient.ensureQueryData(
					authQueryOptions.userInfo(),
				);
				console.log("✅ User info fetched successfully:", userInfo);

				await applyI18nIfNotPreload();

				// If we got user info, the user is authenticated
				// The auth store should update shortly, so allow access
				return;
			} catch (error) {
				console.log("❌ User info fetch failed:", error);

				// User is definitely not authenticated
				const redirectURL = location.href;
				const encodedRedirectURL = encodeURIComponent(redirectURL);

				logApp({
					level: "error",
					message: `User info fetch failed: ${error}`,
				});

				// Redirect to login with return URL
				throw redirect({
					to: "/login",
					search: {
						redirect: encodedRedirectURL,
					},
				});
			}
		}

		// Authenticated but skipped earlier branches (e.g. permissions still loading
		// after the short wait). Without this, translation stays {} and UI shows raw keys.
		const lateAuth = getAuthState();
		if (lateAuth.isAuthenticated && lateAuth.user) {
			await queryClient.ensureQueryData(authQueryOptions.userInfo());
			await applyI18nIfNotPreload();
		}
	},
	validateSearch: AuthenticatedSearchSchema,

	/**
	 * Layout component for protected routes
	 *
	 * This component wraps all protected routes and can include:
	 * - Navigation bars
	 * - User profile displays
	 * - Logout buttons
	 * - Loading states
	 * - Error boundaries
	 */
	component: RouteComponent,

	/**
	 * Loading component shown during authentication checks
	 */
	pendingComponent: AuthLoadingComponent,

	/**
	 * Error component for authentication failures
	 */
	errorComponent: AuthErrorComponent,

	notFoundComponent: NotFound,
});

function RouteComponent() {
	const auth = useAuth();
	const navigate = useNavigate();
	const routerState = useRouterState();
	const { isAuthenticated, isInitializing, isLoading } = useAuthStatus();
	const search = Route.useSearch();
	// const _permissions = useQuery(authQueryOptions.permissions());
	const _activeUrl = routerState.location.pathname;
	void _activeUrl;

	// biome-ignore lint/correctness/useExhaustiveDependencies: everytime it navigates in the auth/callback route, the auth state is reset forcing a redirect to the login route
	const _handleSignOut = useCallback(async () => {
		try {
			await auth.logout();
		} catch (error) {
			console.error("Failed to store auth data:", error);
			logApp({
				level: "error",
				message: `Failed to store auth data: ${error}`,
			});
		} finally {
			navigate({ to: "/signed-out", replace: true });
		}
	}, [auth]);
	void _handleSignOut;

	useEffect(() => {
		if (!isInitializing && !isLoading && !isAuthenticated) {
			// Guard: don't redirect if the router is already on /login or a public path.
			// Without this, the effect re-fires while _authenticated is still mounted during
			// the transition, wrapping the redirect param inside itself on every render tick.
			const currentPath = routerState.location.pathname;
			if (
				currentPath === "/login" ||
				currentPath === "/signed-out" ||
				currentPath === "/account-deleted" ||
				currentPath.startsWith("/auth/")
			) {
				return;
			}
			const redirectTarget = buildLoginRedirect(routerState.location);
			navigate({
				to: "/login",
				search: { redirect: redirectTarget },
				replace: true,
			});
		}
	}, [
		isAuthenticated,
		isInitializing,
		isLoading,
		navigate,
		routerState.location,
	]);

	return (
		<RendererProvider fieldRenderers={appFieldRenderers}>
			<Outlet />
			{search.profileDrawer && <UserProfileDrawer />}
		</RendererProvider>
	);
}

/**
 * Loading component shown during authentication checks
 */
function AuthLoadingComponent() {
	const { t } = useAppTranslation();

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-gray-950"
			data-testid="auth-route-pending"
		>
			<div className="text-center">
				<div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid text-fg-brand-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
				<div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
					{t("authLoading.verifyingAccess")}
				</div>
			</div>
		</div>
	);
}

/**
 * Error component for authentication failures
 */
function AuthErrorComponent({ error }: { error: Error }) {
	const navigate = useNavigate();
	const routerState = useRouterState();
	const { t } = useAppTranslation();
	const isStaleChunk = isMissingChunkError(error);

	useEffect(() => {
		handleMissingChunkError(error);
	}, [error]);

	return (
		<div className="min-h-screen bg-primary flex items-center justify-center">
			<div className="text-center space-y-4 max-w-md">
				<div className="text-error-500 text-6xl">
					{t("authError.icons.lock")}
				</div>
				<h1 className="text-2xl font-bold text-text-primary">
					{isStaleChunk
						? t("admin.error.staleChunkTitle")
						: t("authError.authenticationError")}
				</h1>
				<p className="text-text-secondary">
					{isStaleChunk
						? t("admin.error.staleChunkMessage")
						: error.message ||
							t("authError.anErrorOccurredWhileCheckingAuthentication")}
				</p>
				<Button
					onClick={() => {
						if (isStaleChunk) {
							window.location.reload();
							return;
						}
						const redirectTarget = buildLoginRedirect(routerState.location);
						navigate({
							to: "/login",
							search: { redirect: redirectTarget },
							replace: true,
						});
					}}
					color="primary"
				>
					{isStaleChunk
						? t("admin.error.retryAccess")
						: t("authError.goToLogin")}
				</Button>
			</div>
		</div>
	);
}
